import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Panel,
  useNodesState,
  useEdgesState,
  addEdge,
  Handle,
  Position,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Search, Bot, ShieldCheck, PenTool, Code, Eye, Brain, UserCheck,
  Plus, Save, RotateCcw, Trash2, Play, Settings2, GripVertical,
  Workflow, ArrowRight, Download, Upload, ChevronRight, Sparkles,
  CircleDot, CheckCircle2, Clock, AlertTriangle, X, ChevronDown,
  Layers, Zap, MousePointerClick, LayoutGrid, FolderOpen,
} from 'lucide-react';
import { getAgentConfigs, getWorkflows, createWorkflow, updateWorkflow } from '../lib/api';

/* ═══════════════════════════════════════════════════════════════
   THEME — Matches CipherCampaign dark teal branding
   ═══════════════════════════════════════════════════════════════ */
const STEP_PALETTE = {
  research:   { bg: 'rgba(14, 165, 233, 0.12)', border: 'rgba(14, 165, 233, 0.35)', text: '#38bdf8', icon: Search,      label: 'Researcher' },
  brainstorm: { bg: 'rgba(168, 85, 247, 0.12)', border: 'rgba(168, 85, 247, 0.35)', text: '#c084fc', icon: Brain,       label: 'Brainstorm' },
  content:    { bg: 'rgba(139, 92, 246, 0.12)', border: 'rgba(139, 92, 246, 0.35)', text: '#a78bfa', icon: PenTool,     label: 'Content Writer' },
  html:       { bg: 'rgba(245, 158, 11, 0.12)', border: 'rgba(245, 158, 11, 0.35)', text: '#fbbf24', icon: Code,        label: 'HTML Builder' },
  review:     { bg: 'rgba(16, 185, 129, 0.12)', border: 'rgba(16, 185, 129, 0.35)', text: '#34d399', icon: ShieldCheck,  label: 'CXO Review' },
  approval:   { bg: 'rgba(251, 146, 60, 0.12)', border: 'rgba(251, 146, 60, 0.50)', text: '#fb923c', icon: UserCheck,   label: 'Approval Gate' },
  custom:     { bg: 'rgba(99, 102, 241, 0.12)', border: 'rgba(99, 102, 241, 0.35)', text: '#818cf8', icon: Sparkles,    label: 'Custom Agent' },
};

const NODE_CATALOG = [
  { category: 'Agent Steps', items: [
    { type: 'agentNode', stepType: 'research',   label: 'Researcher',     desc: 'Gather threat intelligence & context' },
    { type: 'agentNode', stepType: 'brainstorm',  label: 'Brainstorm',     desc: 'Strategize messaging & structure' },
    { type: 'agentNode', stepType: 'content',     label: 'Content Writer', desc: 'Draft copy & body text' },
    { type: 'agentNode', stepType: 'html',        label: 'HTML Builder',   desc: 'Produce email-ready HTML' },
    { type: 'agentNode', stepType: 'review',      label: 'CXO Review',     desc: 'Executive review & scoring' },
    { type: 'agentNode', stepType: 'custom',      label: 'Custom Agent',   desc: 'Add your own agent step' },
  ]},
  { category: 'Control Flow', items: [
    { type: 'approvalGate', stepType: 'approval', label: 'Approval Gate',  desc: 'Human-in-the-loop pause point' },
  ]},
];

/* ═══════════════════════════════════════════════════════════════
   CUSTOM NODE — Agent Step
   ═══════════════════════════════════════════════════════════════ */
function AgentNode({ data, selected }) {
  const palette = STEP_PALETTE[data.stepType] || STEP_PALETTE.research;
  const Icon = palette.icon;
  const statusColors = {
    idle: 'bg-white/20',
    running: 'bg-[hsl(var(--brand))] animate-pulse',
    success: 'bg-emerald-400',
    error: 'bg-red-400',
  };

  return (
    <div
      data-testid={`workflow-node-${data.stepType}`}
      className={`group relative rounded-2xl border-2 transition-all duration-200 min-w-[220px] max-w-[240px] ${selected ? 'ring-2 ring-[hsl(var(--brand)/0.5)] ring-offset-2 ring-offset-[hsl(220,18%,7%)] scale-[1.02]' : 'hover:scale-[1.01]'}`}
      style={{ backgroundColor: palette.bg, borderColor: selected ? palette.text : palette.border }}
    >
      <Handle type="target" position={Position.Left} className="!w-3 !h-3 !bg-[hsl(var(--brand))] !border-2 !border-[hsl(220,18%,7%)]" />

      {/* Drag grip */}
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-40 transition-opacity duration-150">
        <GripVertical className="w-3.5 h-3.5 text-white/40" />
      </div>

      {/* Header */}
      <div className="flex items-center gap-2.5 px-4 pt-3.5 pb-2">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${palette.text}20` }}>
          <Icon className="w-4.5 h-4.5" style={{ color: palette.text }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold uppercase tracking-wider truncate" style={{ color: palette.text }}>{data.label}</p>
          <p className="text-[10px] text-muted-foreground/60 truncate">{data.agentName || 'Select agent...'}</p>
        </div>
        <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${statusColors[data.status || 'idle']}`} />
      </div>

      {/* Model tag */}
      {data.model && (
        <div className="px-4 pb-3">
          <span className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[9px] font-medium bg-white/5 border border-white/10 text-muted-foreground/70">
            <Bot className="w-2.5 h-2.5" /> {data.model}
          </span>
        </div>
      )}

      <Handle type="source" position={Position.Right} className="!w-3 !h-3 !bg-[hsl(var(--brand))] !border-2 !border-[hsl(220,18%,7%)]" />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   CUSTOM NODE — Human-in-the-Loop Approval Gate
   ═══════════════════════════════════════════════════════════════ */
function ApprovalGateNode({ data, selected }) {
  const palette = STEP_PALETTE.approval;
  const statusIcons = {
    idle: <Clock className="w-4 h-4 text-muted-foreground/40" />,
    pending: <CircleDot className="w-4 h-4 text-amber-400 animate-pulse" />,
    approved: <CheckCircle2 className="w-4 h-4 text-emerald-400" />,
    rejected: <AlertTriangle className="w-4 h-4 text-red-400" />,
  };

  return (
    <div
      data-testid={`workflow-node-approval-${data.gateId || 'gate'}`}
      className={`group relative rounded-2xl border-2 border-dashed transition-all duration-200 min-w-[200px] max-w-[230px] ${selected ? 'ring-2 ring-amber-400/50 ring-offset-2 ring-offset-[hsl(220,18%,7%)] scale-[1.02]' : 'hover:scale-[1.01]'}`}
      style={{ backgroundColor: palette.bg, borderColor: selected ? palette.text : palette.border }}
    >
      <Handle type="target" position={Position.Left} className="!w-3 !h-3 !bg-amber-400 !border-2 !border-[hsl(220,18%,7%)]" />

      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-40 transition-opacity duration-150">
        <GripVertical className="w-3.5 h-3.5 text-white/40" />
      </div>

      <div className="flex items-center gap-2.5 px-4 py-3.5">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-amber-500/20 shrink-0">
          <UserCheck className="w-4.5 h-4.5 text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold uppercase tracking-wider text-amber-400">Approval Gate</p>
          <p className="text-[10px] text-muted-foreground/60 truncate">{data.label || 'Human review required'}</p>
        </div>
        {statusIcons[data.approvalStatus || 'idle']}
      </div>

      <Handle type="source" position={Position.Right} className="!w-3 !h-3 !bg-amber-400 !border-2 !border-[hsl(220,18%,7%)]" />
    </div>
  );
}

const nodeTypes = {
  agentNode: AgentNode,
  approvalGate: ApprovalGateNode,
};

/* ═══════════════════════════════════════════════════════════════
   DEFAULT PIPELINE — Pre-seeded n8n-style layout
   ═══════════════════════════════════════════════════════════════ */
const DEFAULT_NODES = [
  { id: 'research', type: 'agentNode', position: { x: 80, y: 180 }, data: { label: 'Research', stepType: 'research', agentName: 'Threat Researcher', model: 'claude-sonnet-4', status: 'idle' } },
  { id: 'brainstorm', type: 'agentNode', position: { x: 380, y: 180 }, data: { label: 'Brainstorm', stepType: 'brainstorm', agentName: 'Content Strategist', model: 'claude-sonnet-4', status: 'idle' } },
  { id: 'gate-1', type: 'approvalGate', position: { x: 680, y: 190 }, data: { label: 'Review outline before writing', gateId: 'content_review', approvalStatus: 'idle' } },
  { id: 'content', type: 'agentNode', position: { x: 960, y: 180 }, data: { label: 'Content Writer', stepType: 'content', agentName: 'Email Copywriter', model: 'claude-sonnet-4', status: 'idle' } },
  { id: 'gate-2', type: 'approvalGate', position: { x: 1260, y: 190 }, data: { label: 'Approve draft before HTML', gateId: 'content_draft_review', approvalStatus: 'idle' } },
  { id: 'html', type: 'agentNode', position: { x: 1540, y: 180 }, data: { label: 'HTML Builder', stepType: 'html', agentName: 'HTML Designer', model: 'claude-sonnet-4', status: 'idle' } },
  { id: 'review', type: 'agentNode', position: { x: 1840, y: 180 }, data: { label: 'CXO Review', stepType: 'review', agentName: 'CXO Reviewer', model: 'claude-sonnet-4', status: 'idle' } },
];

const makeEdge = (id, source, target, isApproval = false) => ({
  id,
  source,
  target,
  animated: !isApproval,
  style: isApproval
    ? { stroke: '#fb923c', strokeWidth: 2, strokeDasharray: '8 4' }
    : { stroke: 'hsl(192 72% 44%)', strokeWidth: 2 },
  markerEnd: {
    type: MarkerType.ArrowClosed,
    color: isApproval ? '#fb923c' : 'hsl(192 72% 44%)',
    width: 16,
    height: 16,
  },
});

const DEFAULT_EDGES = [
  makeEdge('e-research-brainstorm', 'research', 'brainstorm'),
  makeEdge('e-brainstorm-gate1', 'brainstorm', 'gate-1'),
  makeEdge('e-gate1-content', 'gate-1', 'content', true),
  makeEdge('e-content-gate2', 'content', 'gate-2'),
  makeEdge('e-gate2-html', 'gate-2', 'html', true),
  makeEdge('e-html-review', 'html', 'review'),
];

/* ═══════════════════════════════════════════════════════════════
   LEFT PALETTE — Draggable node catalog
   ═══════════════════════════════════════════════════════════════ */
function NodePalette({ onAddNode, collapsed, onToggle }) {
  return (
    <div
      data-testid="workflow-node-palette"
      className={`border-r border-white/[0.06] bg-[hsl(var(--pane))] flex flex-col shrink-0 transition-all duration-200 ${collapsed ? 'w-12' : 'w-56'}`}
    >
      {/* Palette header */}
      <div className="flex items-center justify-between px-3 py-3 border-b border-white/[0.06] shrink-0">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-[hsl(var(--brand))]" />
            <span className="text-xs font-semibold text-foreground">Node Palette</span>
          </div>
        )}
        <button
          onClick={onToggle}
          className="p-1 rounded-md hover:bg-white/10 transition-colors duration-150"
          data-testid="toggle-palette-btn"
        >
          <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${collapsed ? '' : 'rotate-180'}`} />
        </button>
      </div>

      {!collapsed && (
        <ScrollArea className="flex-1 p-2">
          {NODE_CATALOG.map(cat => (
            <div key={cat.category} className="mb-4">
              <p className="px-2 mb-2 text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground/40">{cat.category}</p>
              <div className="space-y-1">
                {cat.items.map(item => {
                  const palette = STEP_PALETTE[item.stepType];
                  const Icon = palette?.icon || Sparkles;
                  return (
                    <button
                      key={item.stepType}
                      onClick={() => onAddNode(item.type, item.stepType, item.label)}
                      data-testid={`palette-node-${item.stepType}`}
                      className="w-full group flex items-center gap-2.5 rounded-xl px-2.5 py-2.5 transition-all duration-150 hover:bg-white/[0.06] active:scale-[0.97] cursor-grab"
                    >
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-transform duration-150 group-hover:scale-105"
                        style={{ backgroundColor: `${palette?.text || '#818cf8'}18` }}
                      >
                        <Icon className="w-3.5 h-3.5" style={{ color: palette?.text || '#818cf8' }} />
                      </div>
                      <div className="text-left min-w-0">
                        <p className="text-[11px] font-medium text-foreground/90 truncate">{item.label}</p>
                        <p className="text-[9px] text-muted-foreground/50 truncate leading-tight">{item.desc}</p>
                      </div>
                      <Plus className="w-3 h-3 text-muted-foreground/30 group-hover:text-muted-foreground/60 ml-auto shrink-0 transition-colors duration-150" />
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </ScrollArea>
      )}

      {collapsed && (
        <div className="flex-1 flex flex-col items-center py-3 gap-2">
          {NODE_CATALOG.flatMap(c => c.items).map(item => {
            const palette = STEP_PALETTE[item.stepType];
            const Icon = palette?.icon || Sparkles;
            return (
              <Tooltip key={item.stepType}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => onAddNode(item.type, item.stepType, item.label)}
                    className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/[0.06] transition-colors duration-150"
                    data-testid={`palette-icon-${item.stepType}`}
                  >
                    <Icon className="w-3.5 h-3.5" style={{ color: palette?.text || '#818cf8' }} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">{item.label}</TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   INSPECTOR PANEL — Node configuration sidebar
   ═══════════════════════════════════════════════════════════════ */
function InspectorPanel({ node, agents, onUpdate, onDelete, onClose }) {
  if (!node) return null;
  const isApproval = node.type === 'approvalGate';
  const palette = isApproval ? STEP_PALETTE.approval : (STEP_PALETTE[node.data.stepType] || STEP_PALETTE.research);

  return (
    <div className="w-[300px] border-l border-white/[0.06] bg-[hsl(var(--pane))] flex flex-col shrink-0" data-testid="workflow-inspector">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06] shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${palette.text}20` }}>
            {isApproval
              ? <UserCheck className="w-3.5 h-3.5" style={{ color: palette.text }} />
              : React.createElement(palette.icon, { className: 'w-3.5 h-3.5', style: { color: palette.text } })}
          </div>
          <span className="text-sm font-semibold text-foreground">{isApproval ? 'Approval Gate' : node.data.label}</span>
        </div>
        <button onClick={onClose} className="p-1 rounded-md hover:bg-white/10 transition-colors duration-150" data-testid="inspector-close-btn">
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>

      {/* Config Form */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {isApproval ? (
            <>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-semibold mb-1.5 block">Gate Label</label>
                <Input
                  value={node.data.label || ''}
                  onChange={e => onUpdate(node.id, { ...node.data, label: e.target.value })}
                  className="h-8 text-xs bg-white/5 border-white/10"
                  data-testid="inspector-gate-label"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-semibold mb-1.5 block">Gate Trigger</label>
                <Select value={node.data.gateId || 'content_review'} onValueChange={v => onUpdate(node.id, { ...node.data, gateId: v })}>
                  <SelectTrigger className="h-8 text-xs bg-white/5 border-white/10" data-testid="inspector-gate-id">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="content_review">After Brainstorm</SelectItem>
                    <SelectItem value="content_draft_review">After Content Draft</SelectItem>
                    <SelectItem value="custom">Custom Gate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="rounded-xl bg-amber-500/5 border border-amber-500/15 p-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <MousePointerClick className="w-3.5 h-3.5 text-amber-400" />
                  <span className="text-[10px] font-semibold text-amber-400 uppercase tracking-wider">Human-in-the-Loop</span>
                </div>
                <p className="text-[10px] text-muted-foreground/60 leading-relaxed">
                  This gate pauses the pipeline and waits for human approval. The reviewer can approve, request revisions, or reject the artifact.
                </p>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-semibold mb-1.5 block">Step Name</label>
                <Input
                  value={node.data.label || ''}
                  onChange={e => onUpdate(node.id, { ...node.data, label: e.target.value })}
                  className="h-8 text-xs bg-white/5 border-white/10"
                  data-testid="inspector-step-name"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-semibold mb-1.5 block">Assigned Agent</label>
                <Select value={node.data.agentId || ''} onValueChange={v => {
                  const agent = agents.find(a => a.id === v);
                  onUpdate(node.id, { ...node.data, agentId: v, agentName: agent?.name || '', model: agent?.model_id || '' });
                }}>
                  <SelectTrigger className="h-8 text-xs bg-white/5 border-white/10" data-testid="inspector-agent-select">
                    <SelectValue placeholder="Select agent..." />
                  </SelectTrigger>
                  <SelectContent>
                    {agents.map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-semibold mb-1.5 block">Step Type</label>
                <Select value={node.data.stepType || 'research'} onValueChange={v => {
                  const p = STEP_PALETTE[v];
                  onUpdate(node.id, { ...node.data, stepType: v, label: node.data.label || p?.label || v });
                }}>
                  <SelectTrigger className="h-8 text-xs bg-white/5 border-white/10" data-testid="inspector-step-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="research">Research</SelectItem>
                    <SelectItem value="brainstorm">Brainstorm</SelectItem>
                    <SelectItem value="content">Content Writer</SelectItem>
                    <SelectItem value="html">HTML Builder</SelectItem>
                    <SelectItem value="review">CXO Review</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {node.data.model && (
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-semibold mb-1.5 block">Model</label>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground px-1">
                    <Bot className="w-3 h-3" /> {node.data.model}
                  </div>
                </div>
              )}
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-semibold mb-1.5 block">Prompt Override (optional)</label>
                <Textarea
                  value={node.data.promptOverride || ''}
                  onChange={e => onUpdate(node.id, { ...node.data, promptOverride: e.target.value })}
                  placeholder="Leave empty to use agent's default system prompt..."
                  className="text-xs bg-white/5 border-white/10 min-h-[80px] resize-none"
                  data-testid="inspector-prompt-override"
                />
              </div>
            </>
          )}
        </div>
      </ScrollArea>

      {/* Footer actions */}
      <div className="border-t border-white/[0.06] p-3 shrink-0">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onDelete(node.id)}
          className="w-full gap-2 text-xs border-red-500/15 text-red-400 hover:bg-red-500/10 hover:border-red-500/25"
          data-testid="inspector-delete-node"
        >
          <Trash2 className="w-3 h-3" /> Remove Node
        </Button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT — WorkflowBuilder
   ═══════════════════════════════════════════════════════════════ */
export default function WorkflowBuilder() {
  const [nodes, setNodes, onNodesChange] = useNodesState(DEFAULT_NODES);
  const [edges, setEdges, onEdgesChange] = useEdgesState(DEFAULT_EDGES);
  const [selectedNode, setSelectedNode] = useState(null);
  const [agents, setAgents] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [currentWorkflowId, setCurrentWorkflowId] = useState(null);
  const [workflowName, setWorkflowName] = useState('Default Pipeline');
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [paletteCollapsed, setPaletteCollapsed] = useState(false);
  const reactFlowRef = useRef(null);
  const nodeCountRef = useRef(0);

  // Load agents and workflows
  useEffect(() => {
    (async () => {
      try {
        const res = await getAgentConfigs();
        setAgents(res.data || []);
      } catch (e) { console.warn('Failed to load agents', e); }
      try {
        const res = await getWorkflows();
        setWorkflows(res.data || []);
      } catch (e) { console.warn('Failed to load workflows', e); }
    })();
  }, []);

  const onConnect = useCallback((params) => {
    const isFromApproval = nodes.find(n => n.id === params.source)?.type === 'approvalGate';
    setEdges(eds => addEdge({
      ...params,
      ...makeEdge(`e-${params.source}-${params.target}`, params.source, params.target, isFromApproval),
    }, eds));
    setHasChanges(true);
  }, [setEdges, nodes]);

  const onNodeClick = useCallback((_, node) => {
    setSelectedNode(node);
  }, []);

  const onNodesChangeWrapped = useCallback((changes) => {
    onNodesChange(changes);
    const hasPositionChange = changes.some(c => c.type === 'position' && c.dragging === false);
    if (hasPositionChange) setHasChanges(true);
  }, [onNodesChange]);

  const handleUpdateNodeData = useCallback((nodeId, newData) => {
    setNodes(nds => nds.map(n => n.id === nodeId ? { ...n, data: newData } : n));
    setSelectedNode(prev => prev?.id === nodeId ? { ...prev, data: newData } : prev);
    setHasChanges(true);
  }, [setNodes]);

  const handleDeleteNode = useCallback((nodeId) => {
    setNodes(nds => nds.filter(n => n.id !== nodeId));
    setEdges(eds => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
    setSelectedNode(null);
    setHasChanges(true);
  }, [setNodes, setEdges]);

  const handleAddNode = useCallback((nodeType, stepType, label) => {
    nodeCountRef.current += 1;
    const id = `${stepType}-${Date.now()}-${nodeCountRef.current}`;
    // Calculate position: place in visible area near center
    const yBase = 120 + (nodeCountRef.current % 5) * 80;
    const xBase = 300 + (nodeCountRef.current % 4) * 120;
    const newNode = nodeType === 'approvalGate'
      ? { id, type: 'approvalGate', position: { x: xBase, y: yBase }, data: { label: label || 'New Approval Gate', gateId: 'custom', approvalStatus: 'idle' } }
      : { id, type: 'agentNode', position: { x: xBase, y: yBase }, data: { label: label || 'New Agent', stepType, agentName: '', model: '', status: 'idle' } };
    setNodes(nds => [...nds, newNode]);
    setSelectedNode(newNode);
    setHasChanges(true);
    toast.success(`Added ${label || 'node'}`, { duration: 1500 });
  }, [setNodes]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    const payload = {
      name: workflowName,
      description: `Visual workflow with ${nodes.length} nodes`,
      nodes: nodes.map(n => ({ id: n.id, type: n.type, position: n.position, data: n.data })),
      edges: edges.map(e => ({ id: e.id, source: e.source, target: e.target })),
      steps: nodes.filter(n => n.type === 'agentNode').map((n, i) => ({
        step: i + 1,
        name: n.data.label || n.data.stepType,
        agent_key: n.data.stepType,
        agent_name: n.data.agentName || n.data.label,
        agent_id: n.data.agentId || null,
      })),
    };
    try {
      if (currentWorkflowId) {
        await updateWorkflow(currentWorkflowId, payload);
        toast.success('Workflow updated');
      } else {
        const res = await createWorkflow(payload);
        const newId = res.data?.id;
        setCurrentWorkflowId(newId);
        // Refresh workflow list
        const wfRes = await getWorkflows();
        setWorkflows(wfRes.data || []);
        toast.success('Workflow saved');
      }
      setHasChanges(false);
    } catch (e) {
      toast.error('Failed to save workflow');
      console.error('Save error:', e);
    } finally {
      setSaving(false);
    }
  }, [nodes, edges, workflowName, currentWorkflowId]);

  const handleLoadWorkflow = useCallback((wf) => {
    if (wf.nodes && wf.nodes.length > 0) {
      setNodes(wf.nodes);
      setEdges((wf.edges || []).map(e => ({
        ...e,
        ...makeEdge(e.id, e.source, e.target, false),
      })));
      setWorkflowName(wf.name);
      setCurrentWorkflowId(wf.id);
      setHasChanges(false);
      setSelectedNode(null);
      toast.success(`Loaded "${wf.name}"`, { duration: 2000 });
    } else {
      toast.info('This workflow has no visual layout');
    }
  }, [setNodes, setEdges]);

  const handleReset = useCallback(() => {
    setNodes(DEFAULT_NODES);
    setEdges(DEFAULT_EDGES);
    setSelectedNode(null);
    setCurrentWorkflowId(null);
    setWorkflowName('Default Pipeline');
    setHasChanges(false);
  }, [setNodes, setEdges]);

  return (
    <TooltipProvider>
    <div className="flex flex-col h-full" data-testid="workflow-builder-page">
      {/* ─── Top Toolbar ─── */}
      <div className="flex items-center justify-between px-5 py-2.5 border-b border-white/[0.06] bg-[hsl(var(--pane))] shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[hsl(var(--brand)/0.15)] flex items-center justify-center">
            <Workflow className="w-5 h-5 text-[hsl(var(--brand))]" />
          </div>
          <div>
            <Input
              value={workflowName}
              onChange={e => { setWorkflowName(e.target.value); setHasChanges(true); }}
              className="h-7 text-sm font-semibold bg-transparent border-none focus-visible:ring-0 p-0 w-64"
              data-testid="workflow-name-input"
            />
            <div className="flex items-center gap-2">
              <p className="text-[10px] text-muted-foreground/50">
                {nodes.length} nodes &middot; {edges.length} connections
              </p>
              {hasChanges && (
                <Badge variant="outline" className="h-4 px-1.5 text-[9px] border-amber-500/30 text-amber-400 bg-amber-500/5">
                  Unsaved
                </Badge>
              )}
              {currentWorkflowId && !hasChanges && (
                <Badge variant="outline" className="h-4 px-1.5 text-[9px] border-emerald-500/30 text-emerald-400 bg-emerald-500/5">
                  Saved
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Saved workflows dropdown */}
          {workflows.length > 0 && (
            <Select onValueChange={v => {
              const wf = workflows.find(w => w.id === v);
              if (wf) handleLoadWorkflow(wf);
            }}>
              <SelectTrigger className="h-8 text-xs bg-white/5 border-white/10 w-[180px]" data-testid="load-workflow-select">
                <div className="flex items-center gap-1.5">
                  <FolderOpen className="w-3 h-3 text-muted-foreground/60" />
                  <SelectValue placeholder="Load workflow..." />
                </div>
              </SelectTrigger>
              <SelectContent>
                {workflows.map(wf => (
                  <SelectItem key={wf.id} value={wf.id}>{wf.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <div className="w-px h-6 bg-white/[0.06] mx-1" />

          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" onClick={handleReset} className="h-8 w-8 border-white/10" data-testid="reset-workflow-btn">
                <RotateCcw className="w-3.5 h-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Reset to default pipeline</TooltipContent>
          </Tooltip>

          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="h-8 text-xs gap-1.5 bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand)/0.85)] text-[hsl(var(--brand-foreground))]"
            data-testid="save-workflow-btn"
          >
            {saving ? (
              <><Zap className="w-3.5 h-3.5 animate-spin" /> Saving...</>
            ) : (
              <><Save className="w-3.5 h-3.5" /> Save Workflow</>
            )}
          </Button>
        </div>
      </div>

      {/* ─── Palette + Canvas + Inspector ─── */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Palette */}
        <NodePalette
          onAddNode={handleAddNode}
          collapsed={paletteCollapsed}
          onToggle={() => setPaletteCollapsed(p => !p)}
        />

        {/* ReactFlow Canvas */}
        <div className="flex-1 relative" ref={reactFlowRef}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChangeWrapped}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onPaneClick={() => setSelectedNode(null)}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.25, maxZoom: 0.85 }}
            minZoom={0.2}
            maxZoom={2}
            snapToGrid
            snapGrid={[20, 20]}
            proOptions={{ hideAttribution: true }}
            style={{ backgroundColor: 'hsl(220 18% 7%)' }}
          >
            <Background
              variant="dots"
              gap={20}
              size={1}
              color="rgba(255,255,255,0.03)"
            />
            <Controls
              className="!bg-[hsl(var(--pane))] !border-white/[0.06] !rounded-xl !shadow-xl [&>button]:!bg-transparent [&>button]:!border-white/[0.06] [&>button]:!text-muted-foreground [&>button:hover]:!bg-white/10"
              position="bottom-left"
            />
            <MiniMap
              nodeColor={(node) => {
                if (node.type === 'approvalGate') return '#fb923c';
                const p = STEP_PALETTE[node.data?.stepType];
                return p?.text || '#38bdf8';
              }}
              maskColor="rgba(0,0,0,0.6)"
              style={{
                backgroundColor: 'hsl(220 18% 9%)',
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
              position="bottom-right"
            />

            {/* Legend Panel */}
            <Panel position="top-left" className="!m-3">
              <div className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-[hsl(var(--pane)/0.95)] backdrop-blur-md px-3 py-2">
                <span className="text-[10px] text-muted-foreground/50 uppercase tracking-wider font-semibold">Legend:</span>
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-0.5 bg-[hsl(var(--brand))]" />
                  <span className="text-[10px] text-muted-foreground/60">Auto-flow</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-0.5 border-t-2 border-dashed border-amber-400" />
                  <span className="text-[10px] text-muted-foreground/60">After Approval</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <UserCheck className="w-3 h-3 text-amber-400" />
                  <span className="text-[10px] text-muted-foreground/60">Human Gate</span>
                </div>
              </div>
            </Panel>
          </ReactFlow>
        </div>

        {/* Right Inspector Panel (contextual) */}
        {selectedNode && (
          <InspectorPanel
            node={selectedNode}
            agents={agents}
            onUpdate={handleUpdateNodeData}
            onDelete={handleDeleteNode}
            onClose={() => setSelectedNode(null)}
          />
        )}
      </div>
    </div>
    </TooltipProvider>
  );
}
